import { useTranslation } from 'react-i18next';
import { Badge, Box, Image, Panel, useTheme } from '@salecycle/ui-component-library';
import { PanelContainer, Label, SubLabel, TemplateContainer } from './styles';
import { ITemplate } from 'entities/template/template';
import { useTemplatesListDispatch, useTemplatesListState } from '../context/UseTemplatesListContext';
import { TemplateTypes } from '../constants/templateTypes';
import { useEffect, useState } from 'react';
import { useFeatureFlags } from 'core/app/AppContext';

interface IOwnProps {
  template: ITemplate;
}

export function TemplatePanel({ template }: IOwnProps) {
  const { t } = useTranslation(['common', 'designs']);
  const { flagEnabled } = useFeatureFlags();
  const theme = useTheme();
  const dispatch = useTemplatesListDispatch();
  const { selectedTemplate } = useTemplatesListState();

  const templatetype = t('designs:designGallery.form.templateType.' + template.type);
  const templateSupportedLanguages = (template?.supportedLanguages ?? []).map(x => x.language.toUpperCase()).join(', ');
  const globalSiteId = import.meta.env.VITE_GLOBAL_SITE_ID;
  const [time, setTime] = useState(0);
  useEffect(() => {
    setTime(Date.now());
  }, []);

  const isPopupOrEmbed = template.channel !== 'sms' && template.channel !== 'email' && template.channel !== 'rcs';

  const defaultThumbnailImgSource = '/images/designs/design_gallery/default_template_placeholder.png';
  let thumbnailImgSource = defaultThumbnailImgSource;
  if (template.supportedLanguages?.length) {
    switch (template.type) {
      case TemplateTypes.dataAcquisition:
      case TemplateTypes.mpsEmbed:
      case TemplateTypes.mpsPopup:
      case TemplateTypes.displayOnly:
        thumbnailImgSource = `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${template.siteId}/onsite/${
          template.id
        }/${template.supportedLanguages?.[0]?.language ?? ''}.html.jpg?time=${time}`;
        break;
      case TemplateTypes.sendMyBasket:
        thumbnailImgSource =
          template.channel === 'onsite'
            ? `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${template.siteId}/onsite/${template.id}/${template
                .supportedLanguages?.[0]?.language ?? ''}.html.jpg?time=${time}`
            : `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${template.siteId}/${template.id}/${template
                .supportedLanguages?.[0]?.language ?? ''}.template.jpg?time=${time}`;
        break;
      case TemplateTypes.unsubscribe:
        thumbnailImgSource = `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/unsub/sites/${template.siteId}/${
          template.id
        }/${template.supportedLanguages?.[0]?.language ?? ''}.html.jpg?time=${time}`;
        break;
      case TemplateTypes.globalUnsubscribe:
        thumbnailImgSource = `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/unsub/sites/${globalSiteId}/${
          template.id
        }/${template.supportedLanguages?.[0]?.language ?? ''}.html.jpg?time=${time}`;
        break;
      case TemplateTypes.unsubscribeEmailTracking:
        thumbnailImgSource = `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/unsub/sites/${template.siteId}/${
          template.id
        }/${template.supportedLanguages?.[0]?.language ?? ''}.html.jpg?time=${time}`;
        break;
      default:
        thumbnailImgSource = `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${template.siteId}/${
          template.id
        }/${template.supportedLanguages?.[0]?.language ?? ''}.template.jpg?time=${time}`;
        break;
    }
  }

  const handleClick = () => {
    dispatch({
      type: 'SET_SELECTED_TEMPLATE',
      payload: {
        template: template,
      },
    });
  };

  const selected = () => {
    if (selectedTemplate && selectedTemplate.id === template.id) {
      return 'selected ';
    } else {
      return '';
    }
  };

  const classes = selected();
  return (
    <Panel padding={0} variation="clean">
      <PanelContainer
        data-testid="TemplatePanel"
        data-template-id={template.id}
        className={classes}
        onClick={handleClick}
      >
        <TemplateContainer
          aria-label={selectedTemplate?.templateName ?? '' + t('designs:designGallery.template')}
          className={classes}
          isSelected={selectedTemplate && selectedTemplate.id === template.id}
        >
          <Image
            src={thumbnailImgSource}
            size={220}
            onError={e => {
              e.target.src = defaultThumbnailImgSource;
            }}
          />
        </TemplateContainer>

        <Box stackItems={true} aria-label={t('designs:designGallery.templatePanel.templateInformation')}>
          <Label>{template.templateName}</Label>
          {template.type && <SubLabel>{templatetype}</SubLabel>}
          <div style={{ display: 'flex' }}>
            {template.supportedLanguages !== undefined ? (
              <SubLabel style={{ flexGrow: 1 }}>{templateSupportedLanguages}</SubLabel>
            ) : null}
            {flagEnabled('ld-embedded-content') && isPopupOrEmbed && (
              <Badge
                size="small"
                rounded={4}
                style={{
                  color: theme.designToken.primary700,
                  backgroundColor: theme.designToken.primary50,
                  border: 'none',
                  minWidth: 63,
                }}
              >
                {template.isEmbedded ? 'Embed' : 'Pop-up'}
              </Badge>
            )}
          </div>
        </Box>
      </PanelContainer>
    </Panel>
  );
}
